/**
 * DriveZone — Application Entry Point
 *
 * Sets up:
 *   - React Query client (server state management)
 *   - React Router (client-side routing)
 *   - Sentry error boundary (Task 7 — DSN wired up in production settings)
 *
 * Router structure (routes added per task):
 *   Task 3: /login, /register, /verify-otp
 *   Task 4: /app/search, /app/confirm (protected routes)
 *   Task 5: /subscribe, /subscribe/success, /account
 */

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.tsx'

// ── Sentry Error Tracking ────────────────────────────────────────────────────
// Only initialises when VITE_SENTRY_DSN is set — no-op in development without it.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
if (SENTRY_DSN) {
  Sentry.init({
    dsn:              SENTRY_DSN,
    environment:      import.meta.env.MODE,  // 'development' | 'production'
    tracesSampleRate: 0.2,                   // capture 20% of transactions
    sendDefaultPii:   false,                 // never send emails / IPs to Sentry
  })
}

// ── React Query Client ───────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't refetch if data is less than 5 minutes old
      staleTime: 5 * 60 * 1000,
      // Keep unused data in memory for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Only retry once on failure (not 3 times — faster feedback for users)
      retry: 1,
      // Don't automatically refetch when user switches browser tabs
      refetchOnWindowFocus: false,
    },
  },
})

// ── Mount App ────────────────────────────────────────────────────────────
const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element #root not found in index.html')

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
)
